import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchPendingRegistrations, 
  approveRegistration, 
  rejectRegistration,
  deleteRegistration,
  clearErrors 
} from '../../../store/slices/registrationSlice';
import RegistrationDetailModal from './components/RegistrationDetailModal';
import './Registrations.scss';

const Registrations = () => {
  const dispatch = useDispatch();
  const { 
    registrations, 
    loading, 
    error, 
    approvingId, 
    rejectingId, 
    deletingId 
  } = useSelector(state => state.registration);
  
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  
  // Fetch registrations on component mount
  useEffect(() => {
    dispatch(fetchPendingRegistrations());
    
    return () => {
      dispatch(clearErrors());
    };
  }, [dispatch]);
  
  // Filter registrations based on status
  const filteredRegistrations = registrations.filter(reg => {
    if (statusFilter === 'all') return true;
    return reg.status === statusFilter;
  });
  
  // Calculate counts for each status
  const counts = {
    all: registrations.length,
    pending: registrations.filter(reg => reg.status === 'pending').length,
    approved: registrations.filter(reg => reg.status === 'approved').length,
    rejected: registrations.filter(reg => reg.status === 'rejected').length
  };
  
  const handleViewRegistration = (registration) => {
    setSelectedRegistration(registration);
    setModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRegistration(null);
    // Refresh registrations after modal closes
    dispatch(fetchPendingRegistrations());
  };
  
  const handleApprove = async (registrationId, adminRemarks = '') => {
    try {
      await dispatch(approveRegistration({ registrationId, adminRemarks })).unwrap();
      console.log('✅ Registration approved successfully');
    } catch (error) {
      console.error('❌ Failed to approve registration:', error);
    }
  };
  
  const handleReject = async (registrationId, adminRemarks = '') => {
    try {
      await dispatch(rejectRegistration({ registrationId, adminRemarks })).unwrap();
      console.log('✅ Registration rejected successfully');
    } catch (error) {
      console.error('❌ Failed to reject registration:', error);
    }
  };
  
  const handleDelete = async (registrationId) => {
    if (window.confirm('Are you sure you want to delete this registration? This action cannot be undone.')) {
      try {
        await dispatch(deleteRegistration(registrationId)).unwrap();
        console.log('✅ Registration deleted successfully');
      } catch (error) {
        console.error('❌ Failed to delete registration:', error);
      }
    }
  };
  
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved':
        return 'status-badge approved';
      case 'rejected':
        return 'status-badge rejected';
      case 'pending':
        return 'status-badge pending';
      default:
        return 'status-badge';
    }
  };
  
  const renderStatusFilterButtons = () => {
    const statuses = [
      { value: 'all', label: 'All' },
      { value: 'pending', label: 'Pending' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' }
    ];
    
    return (
      <div className="status-filter-group">
        <span className="filter-label">Filter by status:</span>
        <div className="button-group">
          {statuses.map(status => {
            const count = counts[status.value] || 0;
            return (
              <button
                key={status.value}
                className={`btn-group-item ${statusFilter === status.value ? 'active' : ''}`}
                onClick={() => setStatusFilter(status.value)}
              >
                {status.label} <span className="count-badge">({count})</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };
  
  if (loading && registrations.length === 0) {
    return (
      <div className="registrations-container">
        <div className="page-header">
          <h1>Employee Registrations</h1>
          <p>Manage new employee registration requests</p>
        </div>
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading registrations...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="registrations-container">
        <div className="page-header">
          <h1>Employee Registrations</h1>
          <p>Manage new employee registration requests</p>
        </div>
        <div className="error-message">
          Error loading registrations: {error}
        </div>
      </div>
    );
  }
  
  return (
    <div className="registrations-container">
      <div className="page-header">
        <h1>Employee Registrations</h1>
        <p>Manage new employee registration requests</p>
      </div>
      
      {renderStatusFilterButtons()}
      
      <div className="content-section">
        {filteredRegistrations.length === 0 ? (
          <div className="empty-state">
            {statusFilter === 'all' 
              ? 'No employee registrations found.' 
              : `No ${statusFilter} registrations found.`}
          </div>
        ) : (
          <table className="registrations-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Employee ID</th>
                <th>Designation</th>
                <th>Department</th>
                <th>Submitted</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRegistrations.map(registration => (
                <tr key={registration.id} className={registration.status !== 'pending' ? 'processed-item' : ''}>
                  <td className="employee-cell">
                    <div className="employee-name">{registration.name}</div>
                  </td>
                  <td className="email-cell">
                    <div>{registration.email}</div>
                    {registration.personalEmail && (
                      <div className="personal-email">{registration.personalEmail}</div>
                    )}
                  </td>
                  <td>{registration.empID}</td>
                  <td>{registration.designation}</td>
                  <td>{registration.organogram}</td>
                  <td>{formatDate(registration.submittedAt)}</td>
                  <td>
                    <span className={getStatusBadgeClass(registration.status)}>
                      {registration.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="view-btn"
                        onClick={() => handleViewRegistration(registration)}
                        disabled={approvingId === registration.id || rejectingId === registration.id}
                      >
                        Review
                      </button>
                      
                      {registration.status === 'pending' && (
                        <>
                          <button
                            className="approve-btn"
                            onClick={() => handleApprove(registration.id)}
                            disabled={approvingId === registration.id || rejectingId === registration.id}
                          >
                            {approvingId === registration.id ? (
                              <span className="spinner"></span>
                            ) : 'Approve'}
                          </button>
                          
                          <button
                            className="reject-btn"
                            onClick={() => handleReject(registration.id)}
                            disabled={approvingId === registration.id || rejectingId === registration.id}
                          >
                            {rejectingId === registration.id ? (
                              <span className="spinner"></span>
                            ) : 'Reject'}
                          </button>
                        </>
                      )}
                      
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(registration.id)}
                        disabled={deletingId === registration.id}
                      >
                        {deletingId === registration.id ? (
                          <span className="spinner"></span>
                        ) : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      
      <div className="refresh-section">
        <button 
          className="refresh-button" 
          onClick={() => dispatch(fetchPendingRegistrations())}
          disabled={loading}
        >
          Refresh Data
        </button>
      </div>
      
      {modalOpen && selectedRegistration && (
        <RegistrationDetailModal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          registration={selectedRegistration}
          onApprove={handleApprove}
          onReject={handleReject}
          onDelete={handleDelete}
          isProcessing={
            approvingId === selectedRegistration.id || 
            rejectingId === selectedRegistration.id ||
            deletingId === selectedRegistration.id
          }
        />
      )}
    </div>
  );
};

export default Registrations;
